import { keyframes } from '@emotion/react';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Card, CardContent, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import React from 'react';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

interface MonitoringUpdateCardProps {
  instrumentId: string;
  title: string;
  summary: string;
  timestamp: number;
  storylineId: string;
  storylineTitle?: string;
}

export const MonitoringUpdateCard: React.FC<MonitoringUpdateCardProps> = ({
  instrumentId,
  title,
  summary,
  timestamp,
  storylineId,
  storylineTitle,
}) => {
  const theme = useTheme();
  const timeString = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'visible', // Allow pulse to show
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
              <Chip
                icon={<MergeTypeIcon sx={{ fontSize: '1rem !important' }} />}
                label="STORYLINE UPDATE"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  fontWeight: 700, 
                  borderRadius: 1,
                  height: 24,
                  '& .MuiChip-label': { px: 1, fontSize: '0.7rem', letterSpacing: '0.05em' }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <UpdateIcon sx={{ fontSize: '0.9rem' }} />
                {timeString}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'success.main',
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                    animation: `${pulse} 2s infinite`,
                  }}
                />
                <Typography variant="caption" fontWeight="700" sx={{ letterSpacing: '0.05em' }}>
                  LIVE
                </Typography>
              </Box>
            </Stack>
            
            <Box sx={{ pl: 1, borderLeft: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
              <Typography variant="caption" color="primary.main" fontWeight="600" sx={{ mb: 0.5, display: 'block', letterSpacing: '0.05em' }}>
                INCOMING INTELLIGENCE FOR: {instrumentId}
              </Typography>
              {storylineTitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                  Re: {storylineTitle}
                </Typography>
              )}
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {summary}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
